import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { format, formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import { useEffect, useState } from 'react';
import {
  Clock,
  MapPin,
  Users,
  Bell,
  BellRing,
  Play,
  Radio,
} from 'lucide-react';
import clsx from 'clsx';

import { Event, EventStatus } from '../../types';
import { useAppStore } from '../../stores/appStore';
import { useReminder } from '../../hooks/useEvents';
import { useEventInteraction } from '../../hooks/useEvents';
import { categoryService, Category } from '../../services/category.service';
import { settingsAPI } from '../../services/api';
import { LucideIcon } from '../../utils/iconHelper';

interface EventCardProps {
  event: Event;
  size?: 'compact' | 'default' | 'expanded';
  showCategory?: boolean;
  showProgress?: boolean;
  onClick?: (event: Event) => void;
  className?: string;
}

const EventCard = ({
  event,
  size = 'default',
  showCategory = true,
  showProgress = true,
  onClick,
  className,
}: EventCardProps) => {
  const { t } = useTranslation();
  const { toggleReminder, isReminderSet } = useReminder();
  const { trackInteraction } = useEventInteraction();
  const { getEventProgress, isEventActive } = useAppStore();
  const [categories, setCategories] = useState<Category[]>([]);
  const [showCapacityIndicator, setShowCapacityIndicator] = useState(true);
  const [allowRegistration, setAllowRegistration] = useState(true);

  // Load categories and settings
  useEffect(() => {
    const loadData = async () => {
      // Load categories
      const fetchedCategories = await categoryService.getActiveCategories();
      setCategories(fetchedCategories);
      
      // Load settings
      try {
        const settings = await settingsAPI.getPublicSettings();
        // Handle multiple possible setting keys for backward compatibility
        const showCapacity = settings.events?.showCapacityIndicator 
          ?? settings.showCapacityIndicator 
          ?? settings.features?.show_capacity_indicator 
          ?? true;
        const registrationEnabled = settings.users?.registration_enabled 
          ?? settings.allowRegistration 
          ?? true;
        
        setShowCapacityIndicator(showCapacity);
        setAllowRegistration(registrationEnabled);
      } catch (error) {
        console.error('Error loading settings:', error);
        // Default to true if settings fail to load
        setShowCapacityIndicator(true);
        setAllowRegistration(true);
      }
    };
    loadData();
  }, []);

  const isActive = isEventActive(event);
  const progress = getEventProgress(event);
  const hasReminder = isReminderSet(event.id);
  
  const now = new Date();
  const startTime = new Date(event.startTime);
  const endTime = new Date(event.endTime);
  const isUpcoming = startTime > now;
  const isFinished = endTime < now;

  // Get dynamic category styling based on backend data
  const getCategoryStyle = () => {
    const category = categories.find(cat => cat.name === event.category);
    if (category?.color) {
      return { backgroundColor: category.color };
    }
    // Fallback to a default gradient
    return { background: 'linear-gradient(to bottom right, #667eea, #764ba2)' };
  };

  const getCategoryInfo = () => {
    const category = categories.find(cat => cat.name === event.category);
    return {
      name: category?.name || event.category,
      icon: category?.icon || '🎪',
      color: category?.color || '#667eea'
    };
  };

  const categoryStyle = getCategoryStyle();
  const categoryInfo = getCategoryInfo();

  // Card states
  const cardOpacity = isFinished ? 0.6 : isActive ? 1 : 0.9;
  const cardScale = isActive ? 1.02 : 1;

  // Time display
  const getTimeDisplay = () => {
    if (isActive) {
      return {
        text: t('events.time.endsIn', { 
          time: formatDistanceToNow(endTime, { locale: es }) 
        }),
        className: 'text-green-600 font-semibold',
      };
    } else if (isUpcoming) {
      return {
        text: t('events.time.startsIn', { 
          time: formatDistanceToNow(startTime, { locale: es }) 
        }),
        className: 'text-blue-600',
      };
    } else {
      return {
        text: t('events.time.ended', { 
          time: formatDistanceToNow(endTime, { locale: es }) 
        }),
        className: 'text-gray-500',
      };
    }
  };

  const timeDisplay = getTimeDisplay();

  const handleCardClick = () => {
    trackInteraction(event.id, 'view');
    onClick?.(event);
  };

  const handleReminderClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    toggleReminder(event.id);
    trackInteraction(event.id, 'remind');
  };


  return (
    <motion.div
      className={clsx(
        'card relative cursor-pointer group',
        {
          'h-24': size === 'compact',
          'min-h-[140px]': size === 'default',
          'min-h-[200px]': size === 'expanded',
        },
        className
      )}
      style={{ opacity: cardOpacity, scale: cardScale }}
      onClick={handleCardClick}
      whileHover={{ 
        scale: cardScale * 1.02,
        boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
      }}
      whileTap={{ scale: cardScale * 0.98 }}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: cardOpacity, y: 0 }}
      transition={{ duration: 0.3, ease: 'easeOut' }}
    >
      {/* Category Header */}
      {showCategory && (
        <div className="h-2 rounded-t-2xl" style={categoryStyle} />
      )}

      {/* Event Image Banner */}
      {event.imageUrl && (
        <div className="relative">
          <img
            src={event.imageUrl}
            alt={event.title}
            className={clsx(
              'w-full object-cover',
              size === 'expanded' ? 'h-40' : 'h-32'
            )}
          />
        </div>
      )}

      {/* Live Indicator */}
      {event.isStreaming && isActive && (
        <motion.div
          className={clsx(
            'absolute left-3 bg-red-500 text-white px-2 py-1 rounded-full text-xs font-bold flex items-center space-x-1 z-10',
            event.imageUrl ? 'top-6' : 'top-3'
          )}
          animate={{ scale: [1, 1.05, 1] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <Radio className="w-3 h-3" />
          <span>{t('events.streaming.live')}</span>
        </motion.div>
      )}

      <div className="p-4 space-y-3">
        {/* Header Row */}
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <h3 className={clsx(
              'font-semibold text-gray-800 line-clamp-2',
              {
                'text-sm': size === 'compact',
                'text-base': size === 'default',
                'text-lg': size === 'expanded',
              }
            )}>
              {event.title}
            </h3>
            
            {size !== 'compact' && (
              <p className="text-sm text-gray-600 line-clamp-2 mt-1">
                {event.description}
              </p>
            )}
          </div>

        </div>

        {/* Event Details */}
        <div className="space-y-2">
          {/* Time */}
          <div className="flex items-center space-x-2">
            <Clock className="w-4 h-4 text-gray-400" />
            <span className={clsx('text-sm', timeDisplay.className)}>
              {timeDisplay.text}
            </span>
          </div>

          {/* Time Range */}
          {size !== 'compact' && (
            <div className="text-sm text-gray-500">
              {format(startTime, 'HH:mm', { locale: es })} - {format(endTime, 'HH:mm', { locale: es })}
            </div>
          )}

          {/* Location - Show new locations array or fall back to legacy location field */}
          {(event.EventLocations && event.EventLocations.length > 0) ? (
            <div className="flex items-center space-x-2">
              <MapPin className="w-4 h-4 text-gray-400" />
              <span className="text-sm text-gray-600 truncate">
                {event.EventLocations.map((el, index) => 
                  `${el.Location.name}${index < event.EventLocations.length - 1 ? ', ' : ''}`
                ).join('')}
              </span>
            </div>
          ) : event.location ? (
            <div className="flex items-center space-x-2">
              <MapPin className="w-4 h-4 text-gray-400" />
              <span className="text-sm text-gray-600 truncate">{event.location}</span>
            </div>
          ) : null}

          {/* Capacity */}
          {showCapacityIndicator && event.capacity && size !== 'compact' && (
            <div className="flex items-center space-x-2">
              <Users className="w-4 h-4 text-gray-400" />
              <span className="text-sm text-gray-600">
                {event.currentAttendees || 0} / {event.capacity}
              </span>
            </div>
          )}
        </div>

        {/* Progress Bar */}
        {showProgress && isActive && (
          <div className="space-y-1">
            <div className="flex justify-between text-xs text-gray-500">
              <span>{t('events.status.active')}</span>
              <span>{Math.round(progress)}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <motion.div
                className="bg-green-500 h-2 rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 1, ease: 'easeOut' }}
              />
            </div>
          </div>
        )}

        {/* Streaming CTA */}
        {event.isStreaming && isActive && size !== 'compact' && (
          <motion.button
            className="w-full bg-red-500 hover:bg-red-600 text-white py-2 px-4 rounded-lg font-medium flex items-center justify-center space-x-2 transition-colors"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={(e) => {
              e.stopPropagation();
              // Open streaming URL or trigger streaming modal
              if (event.streamUrl) {
                window.open(event.streamUrl, '_blank');
              }
            }}
          >
            <Play className="w-4 h-4" />
            <span>{t('events.streaming.watchLive')}</span>
          </motion.button>
        )}
      </div>

      {/* Category Badge - moved to top right */}
      {showCategory && (
        <div className={clsx('absolute right-3 z-10', event.imageUrl ? 'top-6' : 'top-3')}>
          <span className="bg-white bg-opacity-90 text-gray-700 text-xs px-2 py-1 rounded-full font-medium flex items-center space-x-1 shadow-sm">
            <LucideIcon name={categoryInfo.icon} size={14} className="text-gray-500" />
            <span>{categoryInfo.name}</span>
          </span>
        </div>
      )}
      
      {/* Reminder Button - positioned on the right side, vertically centered */}
      {!isFinished && (
        <div className="absolute right-3 top-1/2 transform -translate-y-1/2 z-20">
          <motion.button
            className={clsx(
              'p-2.5 rounded-full transition-all duration-200 shadow-lg',
              hasReminder
                ? 'bg-yellow-400 text-white'
                : 'bg-white text-gray-500 hover:bg-gray-100 hover:text-gray-700'
            )}
            onClick={handleReminderClick}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            aria-label={hasReminder ? t('events.actions.removeReminder') : t('events.actions.addReminder')}
          >
            {hasReminder ? (
              <BellRing className="w-5 h-5" />
            ) : (
              <Bell className="w-5 h-5" />
            )}
          </motion.button>
        </div>
      )}
    </motion.div>
  );
};

export default EventCard;